import getColor from "@insite/mobius/utilities/getColor";
import React, { FC, useState, useRef, useEffect } from "react";
import styled from "styled-components";

const TooltipWrapper = styled.div`
    position: relative;
    display: inline-block;
`;

const TooltipTrigger = styled.div`
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
`;

const TooltipContainer = styled.div<{ $position: "top" | "bottom" }>`
    position: absolute;
    ${props => props.$position === "bottom" ? "top: 100%;" : "bottom: 100%;"}
    left: 50%;
    transform: translateX(-50%);
    ${props => props.$position === "bottom" ? "margin-top: 11px;" : "margin-bottom: 11px;"}
    z-index: 1000;
`;

const TooltipBody = styled.div`
    display: inline-block;
    padding: 10px;
    text-align: center;
    min-width: 100px;
    max-width: 300px;
    border-radius: 5px;
    background: ${getColor("common.backgroundContrast")};
    color: ${getColor("common.background")};
`;

const TooltipArrow = styled.div<{ $position: "top" | "bottom" }>`
    position: absolute;
    ${props => props.$position === "bottom" ? "bottom: 100%;" : "top: 100%;"}
    left: 50%;
    transform: translateX(-50%);
    border-width: 6px;
    border-style: solid;
    border-color: ${props => 
        props.$position === "bottom" 
            ? `transparent transparent ${getColor("common.backgroundContrast")(props)} transparent`
            : `${getColor("common.backgroundContrast")(props)} transparent transparent transparent`
    };
`;

export interface RichTooltipProps {
    trigger: React.ReactNode;
    children: React.ReactNode;
    position?: "top" | "bottom";
}

const RichTooltip: FC<RichTooltipProps> = ({ trigger, children, position = "top" }) => {
    const [visible, setVisible] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setVisible(false);
            }
        };

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setVisible(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscape);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscape);
        };
    }, []);

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setVisible(!visible);
    };

    return (
        <TooltipWrapper ref={wrapperRef}>
            <TooltipTrigger onClick={handleClick}>
                {trigger}
            </TooltipTrigger>
            {visible && (
                <TooltipContainer $position={position}>
                    <TooltipArrow $position={position} />
                    <TooltipBody>{children}</TooltipBody>
                </TooltipContainer>
            )}
        </TooltipWrapper>
    );
};

export default RichTooltip;
